import { useContext, useState } from 'react';
import { AppModal } from '../';
import { AppContext } from '../../providers/AppContext';
import { useAudio } from '../../providers/AudioContext';
import { StyledAssetModal } from './AssetModal.styled';
import { AssetView, IconButton, BroadsiderTraits } from '../';
import { theme } from '../../theme';
import { consumeItem } from '../../providers/BasejumpApiProvider';
import { useTokenAuth } from '../../hooks';
import useAppEvent from '../../hooks/useAppEvent';

const AssetTitleButton = ({ onClick, equipped}) => {
  const [hovered, setHovered] = useState(false);

  const icon = equipped
    ? hovered
      ? '/assets/icon_unequip.svg'
      : '/assets/icon_equipped.svg'
    : hovered
      ? '/assets/icon_equipped.svg'
      : '/assets/icon_not_equipped.svg';

  const label = equipped ? 'Equipped' : 'Not Equipped';

  const background = equipped
    ? hovered
      ? theme.baseRed
      : theme.buttonGreen
    : hovered
      ? theme.buttonGreen
      : theme.traitBorder;

  return (
    <div onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)} className='asset-modal-title-button' onClick={() => onClick ? onClick() : false} style={{cursor: 'pointer'}}>
      <div className='asset-modal-title-button-content' style={{ background }}>
        <img src={icon} />
        <div className='asset-modal-title-button-label'>{label}</div>
      </div>
      <div className='asset-modal-title-button-corner asset-modal-title-button-corner-l' />
      <div className='asset-modal-title-button-corner asset-modal-title-button-corner-r' />
    </div>
  );
};

const AssetPreview = ({ asset }) => {
  const is3d = !!asset.getGlbSource();
  return (
    <div className='asset-modal-preview-content'>
      <img className='asset-modal-preview-content-bg' src='/assets/item_bg.png'/>
      <div className='asset-modal-preview-image-wrapper' style={{padding: is3d ? '0' : undefined}}>
        {is3d
          ? (<AssetView asset={asset} />)
          : (<img className='asset-modal-preview-image' src={asset.getImgSource()} />)}
      </div>
    </div>
  );
};

const cropName = (name) => {
  const MAX_NAME_CHARS = 20;
  if (name.length <= MAX_NAME_CHARS) {
    return name;
  }
  return name.substr(0, MAX_NAME_CHARS) + '...';
}

const AssetTitle = ({ asset, equipped, onClickEquip }) => {
  const hasConsume = asset.getEffects().includes('FOOD');
  return (
    <div className='asset-modal-title-holder'>
      <div className='asset-modal-title-stub-short' />
      <div className='asset-modal-title'>{cropName(asset.getName())}</div>
      <div className='asset-modal-title-stub-long' />
      {!hasConsume && <AssetTitleButton equipped={equipped} onClick={onClickEquip}/>}
      <div className='asset-modal-title-stub-short' />
    </div>
  );
};

const chooseClickSound = (asset) => {
  if (asset.getName().toLowerCase().includes('burger')) return 'burger';
  if (asset.getName().toLowerCase().includes('ramen')) return 'ramen';
  if (asset.getName().toLowerCase().includes('drink')) return 'drink';
  return 'button'; 
};

const AssetInfo = ({ asset, equipped = false, onTransfer = () => {} }) => {
  const { basejumpToken } = useTokenAuth();
  const { setSelectedAsset, setErrorData, selectedBroadsider, inventoryItems, setInventoryItems } = useContext(AppContext);
  const [consumeDisabled, setConsumeDisabled] = useState(false);
  const { dispatchAppEvent, subscribeToAppEvent, unsubscribeFromAppEvent } = useAppEvent('avatarModel');

  const onConsume = async () => { 
    setConsumeDisabled(true);
    try {
      const [result] = await Promise.all([
        consumeItem(basejumpToken, selectedBroadsider.getAvatarId(), asset.getItemId()),
        runConsumeAnimation(asset.getGlbSource())
      ]);
      const newItems = inventoryItems.filter((i) => i.getItemId() !== result?.item.id);
      setInventoryItems(newItems);
      setSelectedAsset(null);
    } catch (err) {
      setErrorData({ title: 'Error', description: 'You cannot consume this item!'});
    } finally {
      setConsumeDisabled(false);
    }
  }

  const runConsumeAnimation = (objectPath) => {
     return new Promise((resolve) => {
        const consumeFinishedCallback = ({ type }) => {
          if (type === 'consume_object_finished') {
            unsubscribeFromAppEvent(consumeFinishedCallback);
            resolve();
          }
        };
        subscribeToAppEvent(consumeFinishedCallback);
        
        dispatchAppEvent({
          type: 'consume_object',
          data: objectPath
        });
     });
  }

  const hasConsume = asset.getEffects().includes('FOOD');
  const clickSound = chooseClickSound(asset);
  const traits = asset.getTraits() ? [...asset.getTraits()] : [];
  return (
    <div className='asset-modal-info-holder'>
      <div className='asset-modal-info-decorator'/>
      <div className='asset-modal-info'>
        <div className='asset-modal-info-description'>
          {asset.getDescription()}
        </div>
        <div className='asset-modal-info-traits'>
          <BroadsiderTraits traits={traits} singleRow={true}/>
        </div>
      </div>
      {hasConsume && 
      <div className='asset-modal-consume-button-holder'>
        <IconButton
          clickSound={clickSound}
          disabled={consumeDisabled}
          animated={false}
          onClick={onConsume}>
          Consume
        </IconButton>
      </div>
      }
    </div>
  );
};

const AssetModal = ({ asset }) => {
  const { inventory, equipItem, setSelectedAsset, setTransferAsset } = useContext(AppContext);
  const assetEquipped = inventory.slots.some((s) => s.equipped && s.equipped.getId() === asset.getId());
  const slotToEquip = assetEquipped
    ? inventory.slots.find((s) => s.equipped && s.equipped.getId() === asset.getId()).type
    : (inventory.slots.find((s) => !s.equipped) || inventory.slots[0]).type;

  const onTransfer = (assetToTransfer) => {
    setSelectedAsset(null);
    setTransferAsset(assetToTransfer);
  };

  const { playSFX } = useAudio();
  const onEquip = () => {
    const sound = asset.isEquipped() ? 'unequip' : `equip_${asset.getType()}`;
    playSFX(sound);
    equipItem(asset, slotToEquip, !assetEquipped);
  };

  return (
    <AppModal>
      <StyledAssetModal $equipped={assetEquipped}>
        <AssetPreview asset={asset}/>
        <AssetTitle asset={asset} equipped={assetEquipped} onClickEquip={onEquip}/>
        <AssetInfo asset={asset} equipped={assetEquipped} onTransfer={onTransfer}/>
      </StyledAssetModal>
    </AppModal>
  );
};

export default AssetModal;
